import React, { useState, useEffect } from 'react'
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  CircularProgress,
  Box
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { i18n } from '../../translate/i18n'
import { loadJSON } from '../../helpers/loadJSON'

const frontendGitInfo = loadJSON('/gitinfo.json') || {}

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2)
  },
  title: {
    marginBottom: theme.spacing(2)
  },
  description: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary
  },
  tableContainer: {
    maxHeight: 600
  },
  tableCell: {
    fontSize: '0.875rem'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4)
  },
  errorText: {
    color: theme.palette.error.main,
    textAlign: 'center',
    padding: theme.spacing(4)
  },
  infoBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3)
  }
}))

const DependenciesInfo = () => {
  const classes = useStyles()
  const [dependencies, setDependencies] = useState(null)
  const [gitInfo, setGitInfo] = useState(frontendGitInfo)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadDependencies = async () => {
      try {
        setLoading(true)
        const [dependenciesResponse, gitInfoResponse] = await Promise.all([
          fetch('/dependencies.json'),
          fetch('/gitinfo.json').catch(() => null)
        ])

        if (!dependenciesResponse.ok) {
          throw new Error('Arquivo de dependências não encontrado')
        }

        const data = await dependenciesResponse.json()
        setDependencies(data)

        if (gitInfoResponse && gitInfoResponse.ok) {
          const gitInfoData = await gitInfoResponse.json()
          setGitInfo(gitInfoData || frontendGitInfo)
        }
      } catch (err) {
        console.error('Error loading dependencies:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadDependencies()
  }, [])

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !dependencies) {
    return (
      <Typography className={classes.errorText}>
        {i18n.t('settings.dependencies.notAvailable')}
      </Typography>
    )
  }

  const tagName = `${gitInfo?.tagName || ''}`.trim()
  const branchName = `${gitInfo?.branchName || ''}`.trim()
  const resolvedTagName =
    !tagName || tagName.toUpperCase() === 'N/A' ? branchName || 'N/A' : tagName
  const systemVersion =
    dependencies?.systemVersion || dependencies?.version || resolvedTagName

  return (
    <div className={classes.container}>
      <Typography variant="h5" className={classes.title}>
        {i18n.t('settings.dependencies.title')}
      </Typography>
      <Typography variant="body1" className={classes.description}>
        {i18n.t('settings.dependencies.description')}
      </Typography>

      <Box className={classes.infoBox}>
        <Typography variant="body2">
          <strong>{i18n.t('settings.dependencies.systemVersion')}:</strong>{' '}
          {systemVersion}
        </Typography>
        <Typography variant="body2">
          <strong>{i18n.t('settings.dependencies.tagName')}:</strong>{' '}
          {resolvedTagName}
        </Typography>
        <Typography variant="body2">
          <strong>{i18n.t('settings.dependencies.totalDependencies')}:</strong>{' '}
          {dependencies.totalDependencies}
        </Typography>
        <Typography variant="body2">
          <strong>{i18n.t('settings.dependencies.generatedAt')}:</strong>{' '}
          {new Date(dependencies.generatedAt).toLocaleString()}
        </Typography>
      </Box>

      <TableContainer className={classes.tableContainer} component={Paper}>
        <Table stickyHeader size="small" aria-label="dependencies table">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableCell}>
                <strong>{i18n.t('settings.dependencies.tableName')}</strong>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <strong>{i18n.t('settings.dependencies.tableVersion')}</strong>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <strong>{i18n.t('settings.dependencies.tableLicense')}</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dependencies.dependencies.map(dep => (
              <TableRow key={dep.name} hover>
                <TableCell className={classes.tableCell}>
                  {dep.repository ? (
                    <Link
                      href={dep.repository
                        .replace('git+', '')
                        .replace('.git', '')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {dep.name}
                    </Link>
                  ) : (
                    dep.name
                  )}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {dep.version}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {dep.license}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default DependenciesInfo
